// Booking Location Models (see php versions in LibCal namespace)

class LocationCategory {
    constructor({
        id = 0,
        name = '',
        duration = 0,
        padding = 0
    }) {
        this.id = Number(id);
        this.name = name;
        this.duration = Number(duration);
        this.padding = Number(padding);
    }
}

class LocationUser {
    constructor({
        id = 0,
        name = ''
    }) {
        this.id = id;
        this.name = name;
    }
}

class LocationGroup {
    constructor({
        id = 0,
        name = '',
        categories = [],
        users = []
    }) {
        this.id = Number(id);
        this.name = name;
        this.categories = [];
        categories.forEach((cat) => {
            if (typeof cat === 'object') {
                this.categories.push(new LocationCategory(cat));
            }
        });
        this.users = [];
        users.forEach((user) => {
            if (typeof user === 'object') {
                this.users.push(new LocationUser(user));
            }
        });
    }
    /**
     * @param {Number} catId Category to retrieve
     * @returns {LocationCategory|undefined}
     */
    getCategory(catId) {
        return this.categories.find((el) => {
            return el.id === catId;
        });
    }
    /**
     * @param {Number} userId User to retrieve
     * @returns {LocationUser|undefined}
     */
    getUser(userId) {
        return this.users.find((el) => {
            return el.id === userId;
        });
    }
}


class BookingLocationInfo {
    constructor({
        id = 0,
        domain = '',
        name = '',
        groups = [],
        language = { back: '' } // store some translations
    }) {
        this.id = Number(id);
        this.domain = domain;
        this.name = name;
        this.groups = [];
        groups.forEach((gr) => {
            if (typeof gr === 'object') {
                this.groups.push(new LocationGroup(gr));
            }
        });
        this.language = language;
    }
    /**
     * @param {Number} groupId Group to retrieve
     * @returns {LocationGroup|undefined}
     */
    getGroup(groupId) {
        return this.groups.find((el) => {
            return el.id === groupId;
        });
    }
}

class AvailabilityTime {
    constructor({
        userId = 0,
        start = '',
        startDisplay = '',
        endDisplay = '',
        crc = ''
    }) {
        this.userId = userId;
        this.start = start;
        this.startDisplay = startDisplay;
        this.endDisplay = endDisplay;
        this.crc = crc;
    }
}

class BookingResponse {
    /** @prop {AvailabilityTime|null} */
    #availabiltyTime = null;

    constructor({
        siteId = 0,
        locationId = 0,
        groupId = 0,
        categoryId = 0,
        userId = 0,
        date = '',
        time = ''
    }) {
        this.siteId = siteId;
        this.locationId = locationId;
        this.groupId = groupId;
        this.categoryId = categoryId;
        this.userId = userId;
        this.date = date;
        this.setTime(time);
    }

    setTime(time) {
        if (time instanceof AvailabilityTime) {
            this.#availabiltyTime = time;
            // make sure user is set to match too
            // for "no preference" and all nickname groups you need this so a specific user is associated
            this.userId = time.userId;
        }
    }

    getCrc() {
        return this.#availabiltyTime?.crc;
    }

    getStartTime() {
        return this.#availabiltyTime?.start;
    }
}

class AvailabilityUser {
    constructor({
        id = 0,
        libAuthId = 0,
        libAuthRuleId = 0
    }) {
        this.id = id;
        this.libAuthId = libAuthId;
        this.libAuthRuleId = libAuthRuleId;
    }
}

class BookingAvailability {
    constructor({
        dates = [],
        date = '',
        times = [],
        timezoneNote = '',
        users = [],
        language = { prev: '', next: '' } // store some translations
    }) {
        this.dates = dates;
        this.date = date;
        this.times = [];
        if (Array.isArray(times)) {
            times.forEach((time) => {
                this.times.push(new AvailabilityTime(time));
            });
        }
        this.timezoneNote = timezoneNote;
        this.users = [];
        if (Array.isArray(users)) {
            users.forEach((user) => {
                this.users.push(new AvailabilityUser(user));
            });
        }
        this.language = language;
    }
    /**
     * If all users in the availability are authed
     * we can just send then to a url
     * @returns {Boolean}
     */
    areAllUsersAuthed() {
        const authed = this.users.filter((u) => {
            return u.libAuthId > 0;
        });
        return authed.length === this.users.length;
    }
}

/**
 * LibCal Booking Appointment Parameters
 */
class AppointmentParams {
    constructor({
        groupId = 0,
        categoryId = 0,
        promptGroup = '',
        promptCat = '',
        promptCatUser = '',
        promptUser = '',
        promptDate = '',
        promptTime = '',
        promptNoAvail = '',
        successMsg = '',
        promptAuth = ''
    }) {
        this.groupId = groupId;
        this.categoryId = categoryId;
        this.promptGroup = promptGroup;
        this.promptCat = promptCat;
        this.promptCatUser = promptCatUser;
        this.promptUser = promptUser;
        this.promptDate = promptDate;
        this.promptTime = promptTime;
        this.promptNoAvail = promptNoAvail;
        this.successMsg = successMsg;
        this.promptAuth = promptAuth;
    }

    toJSON() {
        const obj = {};
        if (this.groupId > 0) {
            obj.groupId = this.groupId;
        }
        if (this.categoryId > 0) {
            obj.categoryId = this.categoryId;
        }
        if (this.promptGroup !== '') {
            obj.promptGroup = this.promptGroup;
        }
        if (this.promptCat !== '') {
            obj.promptCat = this.promptCat;
        }
        if (this.promptCatUser !== '') {
            obj.promptCatUser = this.promptCatUser;
        }
        if (this.promptUser !== '') {
            obj.promptUser = this.promptUser;
        }
        if (this.promptDate !== '') {
            obj.promptDate = this.promptDate;
        }
        if (this.promptTime !== '') {
            obj.promptTime = this.promptTime;
        }
        if (this.promptNoAvail !== '') {
            obj.promptNoAvail = this.promptNoAvail;
        }
        if (this.successMsg !== '') {
            obj.successMsg = this.successMsg;
        }
        if (this.promptAuth !== '') {
            obj.promptAuth = this.promptAuth;
        }
        return obj;
    }
}

export {
    BookingLocationInfo,
    BookingResponse,
    BookingAvailability,
    AppointmentParams
};
